import type { PopoverProps } from '@material-tailwind/react';
import {
  addDays,
  addMonths,
  addYears,
  differenceInCalendarDays,
  differenceInCalendarMonths,
} from 'date-fns';
import { zhHK } from 'date-fns/locale';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DateLib } from 'react-day-picker';
import type {
  DateRange,
  Formatters,
  Labels,
  ModifiersClassNames,
} from 'react-day-picker';
import {
  formatTravelDateLabel,
  formatTravelDateRangeLabel,
  getTravelDateRangeSeparator,
} from '../../../utils/formatTravelDateRange';
import { createUnavailableDayButtonComponent } from './components/UnavailableDayButton';
import {
  defaultDoneLabel,
  defaultFormatDate,
  defaultMonthNames,
  defaultPlaceholder,
  defaultWeekdayNames,
} from './constants';
import { useDateRangeClassNames } from './hooks/useDateRangeClassNames';
import { useResolvedMonths } from './hooks/useResolvedMonths';
import type { DateRangePickerStaticTextType } from './i18n';
import type {
  DateRangeFooterState,
  DateRangePickerProps,
  RangeDayPickerProps,
  SheetSummaryState,
} from './types';
import {
  classifyUnavailableDate,
  type UnavailableReason,
} from './unavailableReason';
import {
  areRangesEqual,
  createMonthBoundaryModifierClassNames,
  getMonthKey,
  isLastDayOfMonth,
  normalizeMonth,
  parseTranslationList,
  rangeFromProps,
} from './utils';

const monthBoundaryModifierClassNames = createMonthBoundaryModifierClassNames();

type RangeOnSelectArgs = Parameters<
  NonNullable<RangeDayPickerProps['onSelect']>
>;

type UseDateRangePickerParams = DateRangePickerProps & {
  lang: string;
  staticText: DateRangePickerStaticTextType;
};

/**
 * Vertically scrolling month span of the mobile sheet, frozen when it opens
 * (CMS-1854 §1).
 */
export type SheetMonthSpan = {
  firstMonth: Date;
  lastMonth: Date;
  numberOfMonths: number;
};

/**
 * Upper bound on the months the sheet renders. It exists only to contain the
 * `useResolvedMonths` fallback, which reaches ten years out when the CMS
 * configures no advance window — a cap tight enough to touch a configured
 * range would silently hide selectable dates on mobile while desktop can still
 * page to them. MoneyHero currently runs a 360-day advance window with 360-day
 * trips, i.e. a widest span of about 25 months, so this sits above that.
 */
const SHEET_MAX_MONTHS = 30;

export type UseDateRangePickerResult = {
  isOpen: boolean;
  isSheetLayout: boolean;
  numberOfMonths: number;
  desktopDayPickerProps?: {
    left: RangeDayPickerProps;
    right: RangeDayPickerProps;
  };
  resolvedPlaceholder: string;
  resolvedConfirmLabel: string;
  selectionPhase: 'start' | 'end';
  isSelectionValid: boolean;
  guidanceText?: string;
  footerState: DateRangeFooterState;
  sheetSummary: SheetSummaryState;
  sheetWeekdayNames: readonly string[];
  unavailableReasonText?: string;
  hasSelectedDates: boolean;
  selectedDateLabel?: string;
  startDateLabel: string;
  endDateLabel?: string;
  rangeSeparator: string;
  isRangeSelected: boolean;
  handlePopoverHandler: NonNullable<PopoverProps['handler']>;
  handleTriggerClick: () => void;
  handleSheetClose: () => void;
  handleDoneClick: () => void;
  dayPickerProps: RangeDayPickerProps;
  sheetDayPickerProps: RangeDayPickerProps;
  sheetMonthSpan: SheetMonthSpan;
  /** Index of the month the sheet should scroll to when it opens. */
  sheetScrollToMonthIndex: number;
  canGoToPreviousMonth: boolean;
  canGoToNextMonth: boolean;
  handlePreviousMonthClick: () => void;
  handleNextMonthClick: () => void;
};

const mediaConfig = {
  _: '_',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xxl: 'xxl',
} as const;

export function useDateRangePicker({
  startDate,
  endDate,
  displayVariant = 'default',
  minDate,
  maxDate: sourceMaxDate,
  placeholder,
  onSelect,
  onSelectionStart,
  onUnavailableClick,
  duration,
  size = 'full',
  mode = 'range',
  lang,
  staticText,
}: UseDateRangePickerParams): UseDateRangePickerResult {
  const monthNames = useMemo(
    () => parseTranslationList(staticText.monthNames, defaultMonthNames),
    [staticText.monthNames],
  );
  const weekdayNames = useMemo(
    () => parseTranslationList(staticText.weekdayNames, defaultWeekdayNames),
    [staticText.weekdayNames],
  );
  const sheetWeekdayNames = useMemo(
    () =>
      parseTranslationList(staticText.weekdayNamesShort, defaultWeekdayNames),
    [staticText.weekdayNamesShort],
  );

  const breakpoint = useBreakpoint(mediaConfig);

  // The two-month desktop layout (and its guidance/footer/arrow chrome) is only
  // used at the same width where the inline DesktopTIOptions form appears
  // (Tailwind lg = 1280px = the `xxl` breakpoint here). Below that the picker is
  // shown inside the compact TIOptions modal, and the whole band gets the
  // full-screen sheet (CMS-1854) — a two-month grid with outer arrows would
  // overflow that narrow (~672px) modal.
  const isMobileBreakpoint = breakpoint !== 'xxl';
  const defaultNumberOfMonths = size === 'full' ? 2 : 1;
  const numberOfMonths = isMobileBreakpoint ? 1 : defaultNumberOfMonths;
  const isSheetLayout = isMobileBreakpoint;
  const shouldUseDesktopDualCalendar = !isSheetLayout && numberOfMonths > 1;

  const [isOpen, setIsOpen] = useState(false);

  // Reason shown in the helper line after the user clicks a greyed-out date.
  const [activeUnavailableReason, setActiveUnavailableReason] =
    useState<UnavailableReason | null>(null);

  const isAnnualMode = mode === 'annual';
  const calculateAnnualEndDate = useCallback(
    (date: Date) => addDays(addYears(date, 1), -1),
    [],
  );

  const externalRange = useMemo(() => {
    if (isAnnualMode && startDate) {
      // Annual always completes the range to the auto-computed end date.
      return { from: startDate, to: calculateAnnualEndDate(startDate) };
    }
    // A range-mode value with a start but no end is not a committed selection
    // (single trips need both ends). Treat it as "nothing selected yet" so the
    // picker opens in the start-picking phase and the first click sets a fresh
    // start — otherwise it would open mid-selection and anchor every pick to
    // that lone start date.
    if (!startDate || !endDate) {
      return undefined;
    }
    return rangeFromProps(startDate, endDate);
  }, [calculateAnnualEndDate, endDate, isAnnualMode, startDate]);

  const [committedRange, setCommittedRange] = useState<DateRange | undefined>(
    externalRange,
  );
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(
    externalRange,
  );

  const draftFrom = draftRange?.from;
  const draftTo = draftRange?.to;
  // Mid-selection state: a start is picked, the end is not. The advance-window
  // cap (sourceMaxDate) applies to start dates only, so it is lifted while the
  // user picks the end — the duration window takes over instead.
  const isPickingEnd = Boolean(draftFrom) && !draftTo;

  const annualEndBoundary = useMemo(() => {
    if (!isAnnualMode) {
      return undefined;
    }

    const candidates = [
      sourceMaxDate ?? undefined,
      draftRange?.to ?? undefined,
      committedRange?.to ?? undefined,
      externalRange?.to ?? undefined,
    ].filter(Boolean) as Date[];

    if (candidates.length === 0) {
      return undefined;
    }

    return candidates.reduce((latest, current) =>
      current > latest ? current : latest,
    );
  }, [
    committedRange?.to,
    draftRange?.to,
    externalRange?.to,
    isAnnualMode,
    sourceMaxDate,
  ]);

  const durationEndBoundary = useMemo(() => {
    if (!duration || duration <= 0 || !draftFrom) {
      return undefined;
    }

    return addDays(draftFrom, duration);
  }, [draftFrom, duration]);

  const rangeEndBoundary = useMemo(() => {
    const candidates = [
      sourceMaxDate ?? undefined,
      durationEndBoundary,
      draftRange?.to ?? undefined,
      committedRange?.to ?? undefined,
      externalRange?.to ?? undefined,
    ].filter(Boolean) as Date[];

    if (candidates.length === 0) {
      return undefined;
    }

    return candidates.reduce((latest, current) =>
      current > latest ? current : latest,
    );
  }, [
    committedRange?.to,
    draftRange?.to,
    durationEndBoundary,
    externalRange?.to,
    sourceMaxDate,
  ]);

  const calendarEndMonth = isAnnualMode
    ? (annualEndBoundary ?? sourceMaxDate ?? undefined)
    : rangeEndBoundary;

  const { resolvedDefaultMonth, resolvedEndMonth, resolvedStartMonth } =
    useResolvedMonths({
      endMonth: calendarEndMonth,
      maxDate: calendarEndMonth,
      minDate,
      numberOfMonths,
      selectedFrom: draftRange?.from,
    });

  // The sheet renders every month at once and is scrolled, so it must never
  // lose months under the user's scroll position. The live end boundary does
  // exactly that — it widens to `start + duration` on a pick and narrows again
  // when the user restarts from an earlier date. So the span only ever grows
  // (see the effect below), starting from what the *current* state can reach:
  // while no start is picked that is just the advance window, which is around
  // half the months the widest case needs.
  const computeSheetMonthSpan = useCallback((): SheetMonthSpan => {
    const firstMonth = normalizeMonth(
      resolvedStartMonth ?? minDate ?? resolvedDefaultMonth,
    );

    // How far a trip starting on the picked date can run. Until a start is
    // picked there is nothing to carry out, so the advance window is the end.
    const reachFromDraftStart = draftFrom
      ? isAnnualMode
        ? calculateAnnualEndDate(draftFrom)
        : duration && duration > 0
          ? addDays(draftFrom, duration)
          : draftFrom
      : undefined;

    const candidates = [
      sourceMaxDate ?? undefined,
      reachFromDraftStart,
      draftRange?.to ?? undefined,
      externalRange?.to,
      committedRange?.to,
      // Only reached when no advance window is configured; capped below.
      sourceMaxDate ? undefined : resolvedEndMonth,
    ].filter(Boolean) as Date[];

    const latest = candidates.reduce<Date | undefined>(
      (last, current) => (!last || current > last ? current : last),
      undefined,
    );

    const uncappedMonths = latest
      ? differenceInCalendarMonths(normalizeMonth(latest), firstMonth) + 1
      : 1;
    const spanMonths = Math.min(Math.max(uncappedMonths, 1), SHEET_MAX_MONTHS);

    return {
      firstMonth,
      lastMonth: addMonths(firstMonth, spanMonths - 1),
      numberOfMonths: spanMonths,
    };
  }, [
    calculateAnnualEndDate,
    committedRange?.to,
    draftFrom,
    draftRange?.to,
    duration,
    externalRange?.to,
    isAnnualMode,
    minDate,
    resolvedDefaultMonth,
    resolvedEndMonth,
    resolvedStartMonth,
    sourceMaxDate,
  ]);

  const [sheetMonthSpan, setSheetMonthSpan] = useState<SheetMonthSpan>(
    computeSheetMonthSpan,
  );

  // Grow-only: picking a late start extends the reachable end, and those months
  // are appended below the current scroll position. Restarting from an earlier
  // date shortens the reach again, but the span keeps its high-water mark so
  // nothing the user has already scrolled past disappears under them. The span
  // is recomputed from scratch each time the sheet opens.
  useEffect(() => {
    if (!isSheetLayout || !isOpen) {
      return;
    }

    const needed = computeSheetMonthSpan();

    setSheetMonthSpan((previous) =>
      getMonthKey(needed.lastMonth) > getMonthKey(previous.lastMonth)
        ? needed
        : previous,
    );
  }, [computeSheetMonthSpan, isOpen, isSheetLayout]);

  const clampMonthToBounds = useCallback(
    (month: Date): Date => {
      let normalized = normalizeMonth(month);

      if (resolvedStartMonth) {
        const minMonth = normalizeMonth(resolvedStartMonth);
        if (getMonthKey(normalized) < getMonthKey(minMonth)) {
          normalized = minMonth;
        }
      }

      if (resolvedEndMonth) {
        const maxMonth = normalizeMonth(resolvedEndMonth);
        if (getMonthKey(normalized) > getMonthKey(maxMonth)) {
          normalized = maxMonth;
        }
      }

      return normalized;
    },
    [resolvedEndMonth, resolvedStartMonth],
  );

  const resolveDesktopMonthsFromRange = useCallback(
    (range?: DateRange | null) => {
      const leftSource =
        range?.from ?? range?.to ?? normalizeMonth(resolvedDefaultMonth);
      // Always open on the start month with the next month beside it — the
      // arrow navigation moves both months together, so a non-adjacent pair
      // (start | end months of a long range) could never be re-joined.
      const rightSource = addMonths(leftSource, 1);

      let left = clampMonthToBounds(leftSource);
      let right = clampMonthToBounds(rightSource);

      if (getMonthKey(right) <= getMonthKey(left)) {
        const nextRight = clampMonthToBounds(addMonths(left, 1));
        if (getMonthKey(nextRight) > getMonthKey(left)) {
          right = nextRight;
        } else {
          const fallbackLeft = clampMonthToBounds(addMonths(right, -1));
          if (getMonthKey(right) > getMonthKey(fallbackLeft)) {
            left = fallbackLeft;
          }
        }
      }

      return { left, right };
    },
    [clampMonthToBounds, resolvedDefaultMonth],
  );

  const [desktopMonths, setDesktopMonths] = useState<{
    left: Date;
    right: Date;
  }>(() => resolveDesktopMonthsFromRange(externalRange));

  const lastExternalRangeRef = useRef<DateRange | undefined>(externalRange);
  const previousIsOpenRef = useRef(false);
  const rangeBeforeOpenRef = useRef<DateRange | undefined>(externalRange);
  const closeReasonRef = useRef<'done' | null>(null);
  const lastSelectionMetaRef = useRef<{
    date: Date;
    modifiers: RangeOnSelectArgs[2];
    event: RangeOnSelectArgs[3];
  } | null>(null);

  useEffect(() => {
    if (areRangesEqual(externalRange, lastExternalRangeRef.current)) {
      return;
    }

    lastExternalRangeRef.current = externalRange;

    if (!areRangesEqual(committedRange, externalRange)) {
      setCommittedRange(externalRange);
    }

    if (!areRangesEqual(draftRange, externalRange)) {
      setDraftRange(externalRange);
    }
  }, [committedRange, draftRange, externalRange]);

  useEffect(() => {
    const wasOpen = previousIsOpenRef.current;

    if (!wasOpen && isOpen) {
      const baseline = externalRange ?? committedRange ?? undefined;
      rangeBeforeOpenRef.current = baseline;

      if (!areRangesEqual(draftRange, baseline)) {
        setDraftRange(baseline);
      }

      lastSelectionMetaRef.current = null;
      setActiveUnavailableReason(null);

      if (isSheetLayout) {
        setSheetMonthSpan(computeSheetMonthSpan());
      } else if (shouldUseDesktopDualCalendar) {
        const nextDesktopMonths = resolveDesktopMonthsFromRange(baseline);
        setDesktopMonths((prev) => {
          if (
            getMonthKey(prev.left) === getMonthKey(nextDesktopMonths.left) &&
            getMonthKey(prev.right) === getMonthKey(nextDesktopMonths.right)
          ) {
            return prev;
          }
          return nextDesktopMonths;
        });
      }
    }

    if (wasOpen && !isOpen) {
      if (closeReasonRef.current !== 'done') {
        const fallback = externalRange ?? rangeBeforeOpenRef.current;

        if (!areRangesEqual(draftRange, fallback)) {
          setDraftRange(fallback);
        }

        if (!areRangesEqual(committedRange, fallback)) {
          setCommittedRange(fallback);
        }
      }

      closeReasonRef.current = null;
      setActiveUnavailableReason(null);
    }

    previousIsOpenRef.current = isOpen;
  }, [
    committedRange,
    computeSheetMonthSpan,
    draftRange,
    externalRange,
    isOpen,
    isSheetLayout,
    resolveDesktopMonthsFromRange,
    shouldUseDesktopDualCalendar,
  ]);

  // Switching trip type (single ↔ annual) resets any stale reason helper —
  // the selection itself is reset by the consumer via the external props.
  useEffect(() => {
    setActiveUnavailableReason(null);
  }, [mode]);

  const resolvedPlaceholder =
    placeholder ?? staticText.placeholder ?? defaultPlaceholder;
  const resolvedConfirmLabel = isAnnualMode
    ? (staticText.confirmDepartureDate ?? defaultDoneLabel)
    : (staticText.confirmReturnDate ?? defaultDoneLabel);

  const isChineseLang = lang.startsWith('zh');
  const rangeSeparator = getTravelDateRangeSeparator(lang);

  const formatDisplayDate = useCallback(
    (date: Date | undefined | null): string => {
      if (!date) {
        return '';
      }

      // English keeps the picker's own zero-padded format (`Aug 09, 2026`),
      // which differs from the TI form label's `Aug 9, 2026`.
      return isChineseLang
        ? formatTravelDateLabel(date, lang)
        : defaultFormatDate(date);
    },
    [isChineseLang, lang],
  );

  const isRangeSelected =
    draftFrom !== undefined &&
    draftTo !== undefined &&
    differenceInCalendarDays(draftTo, draftFrom) !== 0;

  const selectionPhase: 'start' | 'end' =
    !isAnnualMode && isPickingEnd ? 'end' : 'start';
  const isSelectionValid = isAnnualMode
    ? Boolean(draftFrom)
    : Boolean(draftFrom && draftTo);

  // Mid-selection guidance shown above the calendar; hidden once the
  // selection is complete.
  let guidanceText: string | undefined;
  if (!isSelectionValid) {
    if (isAnnualMode) {
      guidanceText = staticText.guidanceAnnual;
    } else {
      guidanceText =
        selectionPhase === 'start'
          ? staticText.guidanceStart
          : staticText.guidanceEnd;
    }
  }

  // Footer summary: selected range + inclusive trip length (both travel days
  // count), with empty and start-only states.
  const tripLengthDays =
    draftFrom && draftTo
      ? differenceInCalendarDays(draftTo, draftFrom) + 1
      : undefined;

  const footerState: DateRangeFooterState = useMemo(() => {
    if (draftFrom && draftTo && tripLengthDays !== undefined) {
      const countTemplate =
        tripLengthDays === 1
          ? staticText.footerDayCountOne
          : staticText.footerDayCountOther;
      const suffix = isAnnualMode ? staticText.footerAnnualSuffix : '';

      return {
        rangeText: `${formatDisplayDate(draftFrom)}${rangeSeparator}${formatDisplayDate(draftTo)}`,
        countText: countTemplate
          .replace('{count}', String(tripLengthDays))
          .replace('{suffix}', suffix),
        localTimeText: staticText.footerLocalTime,
      };
    }

    if (draftFrom) {
      return {
        rangeText: formatDisplayDate(draftFrom),
        hintText: staticText.footerPickReturn,
        localTimeText: staticText.footerLocalTime,
      };
    }

    return {
      rangeText: staticText.footerEmpty,
      isEmpty: true,
      localTimeText: staticText.footerLocalTime,
    };
  }, [
    draftFrom,
    draftTo,
    formatDisplayDate,
    isAnnualMode,
    rangeSeparator,
    staticText.footerAnnualSuffix,
    staticText.footerDayCountOne,
    staticText.footerDayCountOther,
    staticText.footerEmpty,
    staticText.footerLocalTime,
    staticText.footerPickReturn,
    tripLengthDays,
  ]);

  const sheetSummary: SheetSummaryState = useMemo(
    () => ({
      startLabel: staticText.sheetStartDateLabel,
      startValue: draftFrom
        ? formatDisplayDate(draftFrom)
        : staticText.sheetEmptyDate,
      endLabel: isAnnualMode
        ? staticText.sheetEndDateLabel
        : staticText.sheetReturnDateLabel,
      endValue: draftTo
        ? formatDisplayDate(draftTo)
        : staticText.sheetEmptyDate,
    }),
    [
      draftFrom,
      draftTo,
      formatDisplayDate,
      isAnnualMode,
      staticText.sheetEmptyDate,
      staticText.sheetEndDateLabel,
      staticText.sheetReturnDateLabel,
      staticText.sheetStartDateLabel,
    ],
  );

  const effectiveMaxDate = isPickingEnd
    ? undefined
    : (sourceMaxDate ?? undefined);

  // Reason classification drives both the greying (the `unavailable`
  // modifier) and the on-click explanation — a single source, so the helper
  // text can never disagree with which dates are greyed out.
  const reasonContext = useMemo(
    () => ({
      minDate,
      maxStartDate: effectiveMaxDate,
      phase: selectionPhase,
      start: draftFrom,
      duration,
      mode,
    }),
    [draftFrom, duration, effectiveMaxDate, minDate, mode, selectionPhase],
  );

  const classifyDate = useCallback(
    (date: Date) => classifyUnavailableDate(date, reasonContext),
    [reasonContext],
  );

  const advanceWindowDays =
    sourceMaxDate && minDate
      ? differenceInCalendarDays(sourceMaxDate, minDate)
      : undefined;

  const resolveReasonText = useCallback(
    (reason: UnavailableReason): string => {
      switch (reason) {
        case 'PAST':
          return staticText.reasonPast;
        case 'ADVANCE_WINDOW':
          return staticText.reasonAdvanceWindow.replace(
            '{count}',
            String(advanceWindowDays ?? 0),
          );
        case 'BEFORE_START':
          return staticText.reasonBeforeStart;
        case 'MAX_DURATION':
          return staticText.reasonMaxDuration.replace(
            '{count}',
            String(duration ?? 0),
          );
      }
    },
    [
      advanceWindowDays,
      duration,
      staticText.reasonAdvanceWindow,
      staticText.reasonBeforeStart,
      staticText.reasonMaxDuration,
      staticText.reasonPast,
    ],
  );

  const getDateReasonText = useCallback(
    (date: Date): string | undefined => {
      const reason = classifyDate(date);
      return reason ? resolveReasonText(reason) : undefined;
    },
    [classifyDate, resolveReasonText],
  );

  const unavailableReasonText = activeUnavailableReason
    ? resolveReasonText(activeUnavailableReason)
    : undefined;

  const mergedClassNames = useDateRangeClassNames();

  const formatters = useMemo<Partial<Formatters>>(
    () => ({
      formatCaption: (month, _options, dateLib) => {
        if (!dateLib) {
          return '';
        }
        const monthIndex = dateLib.getMonth(month);
        const year = dateLib.getYear(month);
        const monthName =
          monthNames[monthIndex] ?? defaultMonthNames[monthIndex];

        if (isChineseLang) {
          return `${year}年${monthIndex + 1}月`;
        }

        return dateLib.getMonthYearOrder() === 'year-first'
          ? `${year} ${monthName}`
          : `${monthName} ${year}`;
      },
      formatWeekdayName: (weekdayDate) => {
        const dayIndex = weekdayDate.getDay();
        return weekdayNames[dayIndex] ?? defaultWeekdayNames[dayIndex];
      },
    }),
    [isChineseLang, monthNames, weekdayNames],
  );

  // Re-implements react-day-picker's default labelDayButton with translatable
  // today / selected affixes, which the default hardcodes in English (CMS-1855).
  const labels = useMemo<Partial<Labels>>(
    () => ({
      labelDayButton: (date, modifiers, options, dateLib) => {
        let label = (dateLib ?? new DateLib(options)).format(date, 'PPPP');
        if (modifiers.today) {
          label = `${staticText.dayLabelToday}, ${label}`;
        }
        if (modifiers.selected) {
          label = `${label}, ${staticText.dayLabelSelected}`;
        }
        return label;
      },
      // The month grid is announced the way its caption reads (2026年7月).
      labelGrid: (date, options, dateLib) =>
        formatters.formatCaption?.(date, options, dateLib) ?? '',
    }),
    [formatters, staticText.dayLabelSelected, staticText.dayLabelToday],
  );

  const mergedModifiers = useMemo(
    () => ({
      firstDayOfMonth: (date: Date) => date.getDate() === 1,
      lastDayOfMonth: (date: Date) => isLastDayOfMonth(date),
      unavailable: (date: Date) => classifyDate(date) !== null,
      // rdp only applies range_start when both ends exist — mark the lone
      // draft start so it keeps the filled chip while the end is picked.
      draftStart: (date: Date) =>
        Boolean(
          isPickingEnd &&
            draftFrom &&
            differenceInCalendarDays(date, draftFrom) === 0,
        ),
    }),
    [classifyDate, draftFrom, isPickingEnd],
  );

  const modifiersClassNames = useMemo<ModifiersClassNames>(() => {
    const result: ModifiersClassNames = {};

    (
      Object.entries(monthBoundaryModifierClassNames) as Array<
        [keyof typeof monthBoundaryModifierClassNames, string]
      >
    ).forEach(([key, className]) => {
      result[key] = result[key] ? `${result[key]} ${className}` : className;
    });

    // Unavailable dates render greyed out and lose the hover affordance, but
    // stay real buttons so a click can explain why the date can't be picked.
    result.unavailable =
      '[&>button]:!text-text/disabled [&>button]:!cursor-not-allowed hover:[&>button]:!border-0';

    // The lone draft start renders as the same filled chip as range_start.
    result.draftStart =
      '[&>button]:!border-primary-800 [&>button]:!bg-primary-800 [&>button]:!text-white';

    return result;
  }, []);

  // Latest-ref so the DayButton component identity stays stable across
  // renders — a changing component type would remount the whole day grid on
  // every selection change (dropping DOM focus). Assigned during render so
  // day buttons read the current classifier within the same commit.
  const getDateReasonTextRef = useRef(getDateReasonText);
  getDateReasonTextRef.current = getDateReasonText;

  const dayButtonComponent = useMemo(
    () =>
      createUnavailableDayButtonComponent({
        getReasonText: (date) => getDateReasonTextRef.current(date),
      }),
    [],
  );

  const mergedComponents = useMemo(
    () => ({
      DayButton: dayButtonComponent,
    }),
    [dayButtonComponent],
  );

  useEffect(() => {
    if (!shouldUseDesktopDualCalendar) {
      return;
    }

    setDesktopMonths((prev) => {
      const nextLeft = clampMonthToBounds(prev.left);
      let nextRight = clampMonthToBounds(prev.right);

      if (getMonthKey(nextRight) <= getMonthKey(nextLeft)) {
        const candidateRight = clampMonthToBounds(addMonths(nextLeft, 1));
        if (getMonthKey(candidateRight) > getMonthKey(nextLeft)) {
          nextRight = candidateRight;
        }
      }

      if (
        getMonthKey(nextLeft) === getMonthKey(prev.left) &&
        getMonthKey(nextRight) === getMonthKey(prev.right)
      ) {
        return prev;
      }

      return { left: nextLeft, right: nextRight };
    });
  }, [clampMonthToBounds, shouldUseDesktopDualCalendar]);

  const minMonthKey = useMemo(
    () =>
      resolvedStartMonth
        ? getMonthKey(normalizeMonth(resolvedStartMonth))
        : undefined,
    [resolvedStartMonth],
  );
  const maxMonthKey = useMemo(
    () =>
      resolvedEndMonth
        ? getMonthKey(normalizeMonth(resolvedEndMonth))
        : undefined,
    [resolvedEndMonth],
  );

  // Month arrows shift both side-by-side desktop months together (CMS-1853
  // Req 5); previous is disabled once the earliest month (today's) is shown.
  // The sheet has no arrows at all — it scrolls (CMS-1854 §1).
  const canGoToPreviousMonth =
    shouldUseDesktopDualCalendar &&
    (minMonthKey === undefined ||
      getMonthKey(desktopMonths.left) > minMonthKey);
  const canGoToNextMonth =
    shouldUseDesktopDualCalendar &&
    (maxMonthKey === undefined ||
      getMonthKey(desktopMonths.right) < maxMonthKey);

  const shiftDesktopMonths = useCallback(
    (offset: number) => {
      setDesktopMonths((prev) => {
        const nextLeft = clampMonthToBounds(addMonths(prev.left, offset));
        let nextRight = clampMonthToBounds(addMonths(prev.right, offset));

        if (getMonthKey(nextRight) <= getMonthKey(nextLeft)) {
          const candidateRight = clampMonthToBounds(addMonths(nextLeft, 1));
          if (getMonthKey(candidateRight) > getMonthKey(nextLeft)) {
            nextRight = candidateRight;
          } else {
            return prev;
          }
        }

        if (
          getMonthKey(nextLeft) === getMonthKey(prev.left) &&
          getMonthKey(nextRight) === getMonthKey(prev.right)
        ) {
          return prev;
        }

        return { left: nextLeft, right: nextRight };
      });
    },
    [clampMonthToBounds],
  );

  const handlePreviousMonthClick = useCallback(() => {
    if (!shouldUseDesktopDualCalendar) {
      return;
    }
    shiftDesktopMonths(-1);
  }, [shiftDesktopMonths, shouldUseDesktopDualCalendar]);

  const handleNextMonthClick = useCallback(() => {
    if (!shouldUseDesktopDualCalendar) {
      return;
    }
    shiftDesktopMonths(1);
  }, [shiftDesktopMonths, shouldUseDesktopDualCalendar]);

  const handleLeftMonthChange = useCallback(
    (month: Date) => {
      if (!shouldUseDesktopDualCalendar) {
        return;
      }

      setDesktopMonths((prev) => {
        const nextLeft = clampMonthToBounds(month);
        if (
          getMonthKey(nextLeft) === getMonthKey(prev.left) ||
          getMonthKey(nextLeft) >= getMonthKey(prev.right)
        ) {
          return prev;
        }

        return { ...prev, left: nextLeft };
      });
    },
    [clampMonthToBounds, shouldUseDesktopDualCalendar],
  );

  const handleRightMonthChange = useCallback(
    (month: Date) => {
      if (!shouldUseDesktopDualCalendar) {
        return;
      }

      setDesktopMonths((prev) => {
        const nextRight = clampMonthToBounds(month);
        if (
          getMonthKey(nextRight) === getMonthKey(prev.right) ||
          getMonthKey(nextRight) <= getMonthKey(prev.left)
        ) {
          return prev;
        }

        return { ...prev, right: nextRight };
      });
    },
    [clampMonthToBounds, shouldUseDesktopDualCalendar],
  );

  const handlePopoverHandler = useCallback<
    NonNullable<PopoverProps['handler']>
  >((value) => {
    setIsOpen((prev) =>
      Boolean(typeof value === 'function' ? value(prev) : value),
    );
  }, []);

  const handleTriggerClick = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleSelect = useCallback<
    NonNullable<RangeDayPickerProps['onSelect']>
  >(
    (range, date, modifiers, event) => {
      if (event && 'persist' in event && typeof event.persist === 'function') {
        event.persist();
      }

      const effectiveModifiers = (modifiers ?? {}) as RangeOnSelectArgs[2];

      const clickedDate = date ?? range?.to ?? range?.from ?? null;

      if (!clickedDate) {
        setDraftRange(undefined);
        lastSelectionMetaRef.current = null;
        return;
      }

      const safeEvent = event ?? ({} as RangeOnSelectArgs[3]);

      // A greyed-out date is never selectable — the click explains why it
      // can't be picked instead (CMS-1853 Req 4).
      if (effectiveModifiers.unavailable) {
        const reason = classifyDate(clickedDate);
        if (reason) {
          setActiveUnavailableReason(reason);
          onUnavailableClick?.(clickedDate, reason);
        }
        return;
      }

      // Any valid pick clears the reason helper.
      setActiveUnavailableReason(null);

      if (isAnnualMode) {
        const annualEndDate = calculateAnnualEndDate(clickedDate);
        const nextRange: DateRange = {
          from: clickedDate,
          to: annualEndDate,
        };

        setDraftRange(nextRange);
        lastSelectionMetaRef.current = {
          date: annualEndDate,
          modifiers: effectiveModifiers,
          event: safeEvent,
        };
        onSelectionStart?.(clickedDate);
        return;
      }

      if (!draftFrom || draftTo) {
        // Start a fresh range: nothing selected yet, or the previous range is
        // complete — the click begins a new one.
        if (sourceMaxDate && clickedDate > sourceMaxDate) {
          return;
        }

        setDraftRange({ from: clickedDate, to: undefined });
        lastSelectionMetaRef.current = {
          date: clickedDate,
          modifiers: effectiveModifiers,
          event: safeEvent,
        };
        onSelectionStart?.(clickedDate);
        return;
      }

      // Picking the end. In the integrated component rdp always passes the
      // `unavailable` modifier, so a date before the start (BEFORE_START) or
      // beyond start+duration (MAX_DURATION) is already intercepted by the
      // early return above and shows the reason helper instead of selecting.
      // These two guards are a defensive fallback only for a raw onSelect call
      // (no modifier) — they keep the committed range valid (never inverted,
      // never longer than the max) rather than silently "capping" as a feature.
      if (differenceInCalendarDays(clickedDate, draftFrom) < 0) {
        return;
      }

      let nextTo = clickedDate;

      if (
        duration &&
        duration > 0 &&
        differenceInCalendarDays(clickedDate, draftFrom) > duration
      ) {
        nextTo = addDays(draftFrom, duration);
      }

      const normalizedRange: DateRange = {
        from: draftFrom,
        to: nextTo,
      };

      setDraftRange(normalizedRange);
      lastSelectionMetaRef.current = {
        date: normalizedRange.to ?? clickedDate,
        modifiers: effectiveModifiers,
        event: safeEvent,
      };
    },
    [
      calculateAnnualEndDate,
      classifyDate,
      draftFrom,
      draftTo,
      duration,
      isAnnualMode,
      onSelectionStart,
      onUnavailableClick,
      sourceMaxDate,
    ],
  );

  const handleDoneClick = useCallback(() => {
    if (!isSelectionValid) {
      return;
    }

    closeReasonRef.current = 'done';

    if (!areRangesEqual(committedRange, draftRange)) {
      setCommittedRange(draftRange);
    }

    rangeBeforeOpenRef.current = draftRange;

    if (onSelect) {
      const nextRange = draftRange;
      const fallbackDate = nextRange?.to ?? nextRange?.from;
      const triggerDate =
        lastSelectionMetaRef.current?.date ?? fallbackDate ?? new Date();
      const modifiers =
        lastSelectionMetaRef.current?.modifiers ?? ({} as RangeOnSelectArgs[2]);
      const event =
        lastSelectionMetaRef.current?.event ?? ({} as RangeOnSelectArgs[3]);

      onSelect(nextRange, triggerDate, modifiers, event);
    }

    lastSelectionMetaRef.current = null;

    setIsOpen(false);
  }, [committedRange, draftRange, isSelectionValid, onSelect]);

  // The sheet closes only through its ✕ control (CMS-1854 §3) — an
  // unconfirmed draft is discarded by the close branch of the open/close
  // effect, same as the desktop popover.
  const handleSheetClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const baseDayPickerProps: RangeDayPickerProps = {
    // Plain month label (no dropdown) — navigation is via the ‹ › arrows.
    captionLayout: 'label',
    components: mergedComponents,
    classNames: mergedClassNames,
    formatters,
    labels,
    // Only the aria labels read this: visible text comes from `formatters`; the
    // dropdown formatters would read it too, were `captionLayout` not 'label'.
    locale: isChineseLang ? zhHK : undefined,
    modifiers: mergedModifiers,
    modifiersClassNames,
    mode: 'range',
    hideNavigation: true,
    onSelect: handleSelect,
    selected: draftRange,
  };

  const dayPickerProps: RangeDayPickerProps = {
    ...baseDayPickerProps,
    defaultMonth: resolvedDefaultMonth,
    endMonth: resolvedEndMonth,
    numberOfMonths,
    startMonth: resolvedStartMonth,
  };

  // `endMonth` has to match the frozen span exactly: rdp's `getDisplayMonths`
  // stops as soon as a month passes `endMonth`, so a wider `numberOfMonths`
  // alone would be silently truncated back to the live boundary. Matching them
  // also keeps `getInitialMonth` from shifting the first month backwards.
  const sheetDayPickerProps: RangeDayPickerProps = {
    ...baseDayPickerProps,
    defaultMonth: sheetMonthSpan.firstMonth,
    endMonth: sheetMonthSpan.lastMonth,
    numberOfMonths: sheetMonthSpan.numberOfMonths,
    startMonth: sheetMonthSpan.firstMonth,
  };

  // Reopening on a committed range starts at its month rather than at the top.
  const sheetScrollToMonthIndex = (() => {
    const target = committedRange?.from ?? externalRange?.from;
    if (!target) {
      return 0;
    }
    const index = differenceInCalendarMonths(
      normalizeMonth(target),
      sheetMonthSpan.firstMonth,
    );
    return Math.min(Math.max(index, 0), sheetMonthSpan.numberOfMonths - 1);
  })();

  let desktopDayPickerProps: UseDateRangePickerResult['desktopDayPickerProps'];

  if (shouldUseDesktopDualCalendar) {
    const normalizedLeftMonth = clampMonthToBounds(desktopMonths.left);
    const normalizedRightMonth = clampMonthToBounds(desktopMonths.right);
    const normalizedStartMonth = resolvedStartMonth
      ? normalizeMonth(resolvedStartMonth)
      : undefined;
    const normalizedEndMonth = resolvedEndMonth
      ? normalizeMonth(resolvedEndMonth)
      : undefined;
    const normalizedLeftStartMonth = normalizedStartMonth;
    const monthBeforeRight = normalizeMonth(
      addMonths(normalizedRightMonth, -1),
    );
    const monthAfterLeft = normalizeMonth(addMonths(normalizedLeftMonth, 1));
    const leftEndMonth =
      normalizedEndMonth &&
      getMonthKey(normalizedEndMonth) < getMonthKey(monthBeforeRight)
        ? normalizedEndMonth
        : monthBeforeRight;
    const safeLeftEndMonth =
      normalizedLeftStartMonth &&
      getMonthKey(leftEndMonth) < getMonthKey(normalizedLeftStartMonth)
        ? normalizedLeftStartMonth
        : leftEndMonth;
    const rightStartMonthBase =
      normalizedStartMonth &&
      getMonthKey(normalizedStartMonth) > getMonthKey(monthAfterLeft)
        ? normalizedStartMonth
        : monthAfterLeft;
    const rightStartMonth =
      normalizedEndMonth &&
      getMonthKey(rightStartMonthBase) > getMonthKey(normalizedEndMonth)
        ? normalizedEndMonth
        : rightStartMonthBase;

    desktopDayPickerProps = {
      left: {
        ...baseDayPickerProps,
        defaultMonth: normalizedLeftMonth,
        endMonth: safeLeftEndMonth,
        month: normalizedLeftMonth,
        numberOfMonths: 1,
        onMonthChange: handleLeftMonthChange,
        startMonth: resolvedStartMonth,
      },
      right: {
        ...baseDayPickerProps,
        defaultMonth: normalizedRightMonth,
        endMonth: resolvedEndMonth,
        month: normalizedRightMonth,
        numberOfMonths: 1,
        onMonthChange: handleRightMonthChange,
        startMonth: rightStartMonth,
      },
    };
  }

  const displayRange =
    isOpen && (draftRange?.from || draftRange?.to)
      ? (draftRange ?? committedRange)
      : committedRange;
  const selectedDateLabel = useMemo(() => {
    if (displayVariant !== 'travel' || !displayRange?.from) {
      return undefined;
    }

    return formatTravelDateRangeLabel(
      displayRange.from,
      displayRange.to ?? null,
      lang,
    );
  }, [displayRange, displayVariant, lang]);
  const startDateLabel = formatDisplayDate(displayRange?.from);
  const endDateLabel = displayRange?.to
    ? formatDisplayDate(displayRange?.to)
    : undefined;
  const hasSelectedDates = Boolean(
    selectedDateLabel || startDateLabel || endDateLabel,
  );

  return {
    isOpen,
    isSheetLayout,
    numberOfMonths,
    desktopDayPickerProps,
    resolvedPlaceholder,
    resolvedConfirmLabel,
    selectionPhase,
    isSelectionValid,
    guidanceText,
    footerState,
    sheetSummary,
    sheetWeekdayNames,
    unavailableReasonText,
    hasSelectedDates,
    selectedDateLabel,
    startDateLabel,
    endDateLabel,
    rangeSeparator,
    isRangeSelected,
    handlePopoverHandler,
    handleTriggerClick,
    handleSheetClose,
    handleDoneClick,
    dayPickerProps,
    sheetDayPickerProps,
    sheetMonthSpan,
    sheetScrollToMonthIndex,
    canGoToPreviousMonth,
    canGoToNextMonth,
    handlePreviousMonthClick,
    handleNextMonthClick,
  };
}
